import { useState, useEffect, useContext } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Navigation } from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Plus, Edit, Trash2, Eye, Save, X, ImagePlus, ExternalLink, LogOut } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AdminAuthContext } from "../App";

type Product = {
  name: string;
  image: string;
  description: string;
  price: string;
  rating: number;
  pros: string[];
  cons: string[];
  buyLink: string;
};

type BlogPost = {
  title: string;
  category: string;
  excerpt: string;
  content: string;
  status: "draft" | "published";
  products: Product[];
};

const Admin = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const { toast } = useToast();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editPostId, setEditPostId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setIsAdmin } = useContext(AdminAuthContext);

  const form = useForm<BlogPost>({
    defaultValues: {
      title: "",
      category: "",
      excerpt: "",
      content: "",
      status: "draft",
      products: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "products"
  });

  // Fetch posts from backend
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch("/backend/posts.php");
      const data = await res.json();
      setPosts(data);
    } catch (err) {
      toast({ title: "Error", description: "Failed to fetch posts." });
    }
    setLoading(false);
  };

  // Session check interval
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch("/backend/admin_auth.php", { credentials: "include" });
        const data = await res.json();
        if (!data.authenticated) {
          setIsAdmin(false);
          navigate("/admin-login");
          toast({ 
            title: "Session Expired", 
            description: "Your session has expired. Please log in again.",
            variant: "destructive"
          });
        }
      } catch (error) {
        setIsAdmin(false);
        navigate("/admin-login");
      }
    };

    // Check session every 5 minutes
    const sessionInterval = setInterval(checkSession, 5 * 60 * 1000);
    
    // Initial session check
    checkSession();

    return () => clearInterval(sessionInterval);
  }, [navigate, setIsAdmin, toast]);

  useEffect(() => {
    fetchPosts();
  }, []);

  const onSubmit = async (data: BlogPost) => {
    setLoading(true);
    try {
      // Step 1: Save the blog post
      let res;
      if (editPostId) {
        res = await fetch(`/backend/posts.php?id=${editPostId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: data.title,
            category: data.category,
            excerpt: data.excerpt,
            content: data.content,
            status: data.status
          }),
        });
      } else {
        res = await fetch("/backend/posts.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: data.title,
            category: data.category,
            excerpt: data.excerpt,
            content: data.content,
            status: data.status
          }),
        });
      }
      
      if (!res.ok) {
        toast({ title: "Error", description: "Failed to save post." });
        setLoading(false);
        return;
      }

      const postResult = await res.json();
      const postId = editPostId || postResult.id;

      // Step 2: If editing, delete existing products and their pros/cons
      if (editPostId) {
        try {
          const existingProductsRes = await fetch(`/backend/products.php?post_id=${postId}`);
          if (existingProductsRes.ok) {
            const existingProducts = await existingProductsRes.json();
            for (const product of existingProducts) {
              // Delete pros/cons for this product
              await fetch(`/backend/product_pros_cons.php?product_id=${product.id}`, {
                method: "DELETE"
              });
              // Delete the product
              await fetch(`/backend/products.php?id=${product.id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (err) {
          console.error("Error deleting existing products:", err);
        }
      }

      // Step 3: Save products (only if there are products)
      if (data.products && data.products.length > 0) {
        for (let i = 0; i < data.products.length; i++) {
          const product = data.products[i];
          
          // Save product
          const productRes = await fetch("/backend/products.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              post_id: postId,
              name: product.name,
              image: product.image,
              description: product.description,
              price: product.price,
              rating: product.rating,
              buyLink: product.buyLink ? (product.buyLink.startsWith('http') ? product.buyLink : 'https://' + product.buyLink) : ''
            }),
          });

          if (productRes.ok) {
            const productResult = await productRes.json();
            const productId = productResult.id;

            // Step 4: Save pros
            if (product.pros && product.pros.length > 0) {
              for (const pro of product.pros) {
                if (pro.trim()) {
                  await fetch("/backend/product_pros_cons.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      product_id: productId,
                      type: "pro",
                      text: pro.trim()
                    }),
                  });
                }
              }
            }

            // Step 5: Save cons
            if (product.cons && product.cons.length > 0) {
              for (const con of product.cons) {
                if (con.trim()) {
                  await fetch("/backend/product_pros_cons.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      product_id: productId,
                      type: "con",
                      text: con.trim()
                    }),
                  });
                }
              }
            }
          }
        }
      }

      toast({ title: "Post Saved", description: `Your blog post "${data.title}" has been saved successfully.` });
      setIsEditing(false);
      setEditPostId(null);
      form.reset();
      fetchPosts();
    } catch (err) {
      toast({ title: "Error", description: "Failed to save post." });
    }
    setLoading(false);
  };

  const handleNewPost = () => {
    form.reset({
      title: "",
      category: "",
      excerpt: "",
      content: "",
      status: "draft",
      products: []
    });
    setIsEditing(true);
    setActiveTab("editor");
  };

  const handleEditPost = async (post: any) => {
    setIsEditing(true);
    setActiveTab("editor");
    setEditPostId(post.id);

    // Load products for this post
    try {
      const productsRes = await fetch(`/backend/products.php?post_id=${post.id}`);
      if (productsRes.ok) {
        const products = await productsRes.json();
        
        // Load pros and cons for each product
        const productsWithProsCons = await Promise.all(
          products.map(async (product: any) => {
            const prosConsRes = await fetch(`/backend/product_pros_cons.php?product_id=${product.id}`);
            if (prosConsRes.ok) {
              const prosCons = await prosConsRes.json();
              const pros = prosCons.filter((item: any) => item.type === 'pro').map((item: any) => item.text);
              const cons = prosCons.filter((item: any) => item.type === 'con').map((item: any) => item.text);
              return {
                name: product.name,
                image: product.image,
                description: product.description,
                price: product.price,
                rating: product.rating,
                buyLink: product.buyLink,
                pros: pros.length > 0 ? pros : [""],
                cons: cons.length > 0 ? cons : [""]
              };
            }
            return {
              name: product.name,
              image: product.image,
              description: product.description,
              price: product.price,
              rating: product.rating,
              buyLink: product.buyLink,
              pros: [""],
              cons: [""]
            };
          })
        );

        form.reset({
          title: post.title,
          category: post.category,
          excerpt: post.excerpt,
          content: post.content,
          status: post.status,
          products: productsWithProsCons
        });
      } else {
        // No products found, reset with empty products array
        form.reset({
          title: post.title,
          category: post.category,
          excerpt: post.excerpt,
          content: post.content,
          status: post.status,
          products: []
        });
      }
    } catch (err) {
      // Error loading products, reset with empty products array
      form.reset({
        title: post.title,
        category: post.category,
        excerpt: post.excerpt,
        content: post.content,
        status: post.status,
        products: []
      });
    }
  };

  const handleDeletePost = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    setLoading(true);
    try {
      const res = await fetch(`/backend/posts.php?id=${id}`, { method: "DELETE" });
      if (res.ok) {
        toast({ title: "Post Deleted", description: "The post has been deleted." });
        fetchPosts();
      } else {
        toast({ title: "Error", description: "Failed to delete post." });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete post." });
    }
    setLoading(false);
  };

  const addProduct = () => {
    append({
      name: "",
      image: "",
      description: "",
      price: "",
      rating: 5,
      pros: [""],
      cons: [""],
      buyLink: ""
    });
  };

  const addProCon = (productIndex: number, type: "pros" | "cons") => {
    const currentProducts = form.getValues("products");
    const updatedProducts = [...currentProducts];
    updatedProducts[productIndex][type].push("");
    form.setValue("products", updatedProducts);
  };

  const removeProCon = (productIndex: number, type: "pros" | "cons", index: number) => {
    const currentProducts = form.getValues("products");
    const updatedProducts = [...currentProducts];
    updatedProducts[productIndex][type].splice(index, 1);
    form.setValue("products", updatedProducts);
  };

  const handleLogout = async () => {
    try {
      await fetch("/backend/admin_auth.php", { 
        method: "DELETE", 
        credentials: "include" 
      });
      setIsAdmin(false);
      navigate("/");
      toast({ 
        title: "Logged Out", 
        description: "You have been successfully logged out." 
      });
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Failed to logout. Please try again." 
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage your blog posts and affiliate content</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleNewPost} className="bg-primary">
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <Tabs defaultValue="posts" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="posts">Blog Posts</TabsTrigger>
            <TabsTrigger value="editor">Editor</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>All Posts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loading ? (
                    <p>Loading posts...</p>
                  ) : posts.length === 0 ? (
                    <p>No posts found. Create a new one!</p>
                  ) : (
                    posts.map((post) => (
                    <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold">{post.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary">{post.category}</Badge>
                            <Badge variant={post.status === "published" ? "default" : "outline"}>{post.status}</Badge>
                            <span className="text-sm text-muted-foreground">{post.created_at}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                          <Button variant="outline" size="sm" onClick={() => handleEditPost(post)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeletePost(post.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="editor" className="space-y-6">
            {isEditing ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Blog Post Editor</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Basic Post Information */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="title"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Post Title</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter post title..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="category"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Category</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select category" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="smartphones">Smartphones</SelectItem>
                                  <SelectItem value="laptops">Laptops</SelectItem>
                                  <SelectItem value="cameras">Cameras</SelectItem>
                                  <SelectItem value="headphones">Headphones</SelectItem>
                                  <SelectItem value="tablets">Tablets</SelectItem>
                                  <SelectItem value="wearables">Wearables</SelectItem>
                                  <SelectItem value="gaming">Gaming</SelectItem>
                                  <SelectItem value="audio">Audio & Speakers</SelectItem>
                                  <SelectItem value="monitors">Monitors & Displays</SelectItem>
                                  <SelectItem value="keyboards">Keyboards & Mice</SelectItem>
                                  <SelectItem value="storage">Storage & Memory</SelectItem>
                                  <SelectItem value="networking">Networking</SelectItem>
                                  <SelectItem value="smart-home">Smart Home</SelectItem>
                                  <SelectItem value="drones">Drones & RC</SelectItem>
                                  <SelectItem value="accessories">Accessories</SelectItem>
                                  <SelectItem value="software">Software & Apps</SelectItem>
                                  <SelectItem value="fashion">Fashion</SelectItem>
                                  <SelectItem value="jewelry">Jewelry & Accessories</SelectItem>
                                  <SelectItem value="gadgets">Gadgets & Tools</SelectItem>
                                  <SelectItem value="pc">PC</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Status</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="draft">Draft</SelectItem>
                                  <SelectItem value="published">Published</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="excerpt"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Excerpt</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Brief description of the post..." 
                                className="resize-none"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="content"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Content</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Write your blog post introduction and overview here..."
                                className="min-h-[200px] resize-none"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Products Section */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Products ({fields.length})</CardTitle>
                        <Button type="button" onClick={addProduct} variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Product
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {fields.map((field, index) => (
                        <Card key={field.id} className="border-border">
                          <CardHeader className="pb-4">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg">Product {index + 1}</CardTitle>
                              <Button 
                                type="button" 
                                onClick={() => remove(index)} 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive hover:text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Product Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name={`products.${index}.name`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Product Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="e.g., iPhone 15 Pro" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`products.${index}.price`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Price</FormLabel>
                                    <FormControl>
                                      <Input placeholder="$999" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name={`products.${index}.image`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Product Image URL</FormLabel>
                                    <FormControl>
                                      <Input 
                                        placeholder="https://example.com/image.jpg" 
                                        {...field} 
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`products.${index}.rating`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Rating (1-5)</FormLabel>
                                    <Select onValueChange={(value) => field.onChange(Number(value))} defaultValue={field.value?.toString()}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="5">5 Stars</SelectItem>
                                        <SelectItem value="4.5">4.5 Stars</SelectItem>
                                        <SelectItem value="4">4 Stars</SelectItem>
                                        <SelectItem value="3.5">3.5 Stars</SelectItem>
                                        <SelectItem value="3">3 Stars</SelectItem>
                                        <SelectItem value="2.5">2.5 Stars</SelectItem>
                                        <SelectItem value="2">2 Stars</SelectItem>
                                        <SelectItem value="1.5">1.5 Stars</SelectItem>
                                        <SelectItem value="1">1 Star</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={form.control}
                              name={`products.${index}.description`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Product Description</FormLabel>
                                  <FormControl>
                                    <Textarea 
                                      placeholder="Detailed description of the product..." 
                                      className="resize-none"
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`products.${index}.buyLink`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Amazon Affiliate Link</FormLabel>
                                  <FormControl>
                                    <Input 
                                      placeholder="https://amazon.com/your-affiliate-link" 
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Pros and Cons */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Pros */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium text-success">Pros</Label>
                                  <Button 
                                    type="button" 
                                    onClick={() => addProCon(index, "pros")} 
                                    variant="ghost" 
                                    size="sm"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                {form.watch(`products.${index}.pros`)?.map((_, proIndex) => (
                                  <div key={proIndex} className="flex gap-2">
                                    <FormField
                                      control={form.control}
                                      name={`products.${index}.pros.${proIndex}`}
                                      render={({ field }) => (
                                        <FormItem className="flex-1">
                                          <FormControl>
                                            <Input 
                                              placeholder={`Pro ${proIndex + 1}`} 
                                              {...field} 
                                              className="text-sm"
                                            />
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                    <Button 
                                      type="button" 
                                      onClick={() => removeProCon(index, "pros", proIndex)} 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>

                              {/* Cons */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium text-destructive">Cons</Label>
                                  <Button 
                                    type="button" 
                                    onClick={() => addProCon(index, "cons")} 
                                    variant="ghost" 
                                    size="sm"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                {form.watch(`products.${index}.cons`)?.map((_, conIndex) => (
                                  <div key={conIndex} className="flex gap-2">
                                    <FormField
                                      control={form.control}
                                      name={`products.${index}.cons.${conIndex}`}
                                      render={({ field }) => (
                                        <FormItem className="flex-1">
                                          <FormControl>
                                            <Input 
                                              placeholder={`Con ${conIndex + 1}`} 
                                              {...field} 
                                              className="text-sm"
                                            />
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                    <Button 
                                      type="button" 
                                      onClick={() => removeProCon(index, "cons", conIndex)} 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {index < fields.length - 1 && <Separator className="mt-6" />}
                          </CardContent>
                        </Card>
                      ))}

                      {fields.length === 0 && (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground mb-4">No products added yet</p>
                          <Button type="button" onClick={addProduct} variant="outline">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Your First Product
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Submit Buttons */}
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex gap-4 justify-end">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            setIsEditing(false);
                            form.reset();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          className="bg-primary hover:bg-primary/90"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Blog Post
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </form>
              </Form>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <p className="text-muted-foreground mb-4">Select a post to edit or create a new one</p>
                  <Button onClick={handleNewPost}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Post
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      <Footer />
    </div>
  );
};

export default Admin;